import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';
import { getCustomerByIdV2, getCustomerHighlightsByIdV2, getCustomerLoyaltyHistory, getCustomerOrdersV2, getCustomersV2 } from '../../services/customer.service';
import { ICustomer } from '../../interfaces/Customer';
const listQueryKey = 'customers';

interface IQueryFnParams {
  customers: ICustomer[];
  totalRowsCount: number;
}

interface IQueryOrdersFnParams {
  orders: ICustomer[];
  totalRowsCount: number;
}

export const useGetCustomers = (query: {
  searchTerm?: string
  name?: string
  phone?: string
  email?: string
  fromDate?: string
  toDate?: string
  limit: number,
  createdIn?: string
  includeOrderMetrics?: string
}, enabled = true, options?: { scope?: string }) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => [
    listQueryKey,
    ...(options?.scope ? [{ scope: options.scope }] : []),
    query.limit,
    query.searchTerm,
    query.name,
    query.phone,
    query.email,
    query.fromDate,
    query.toDate,
    query.createdIn,
    query.includeOrderMetrics
  ], [
    options?.scope,
    query.limit,
    query.searchTerm,
    query.name,
    query.phone,
    query.email,
    query.fromDate,
    query.toDate,
    query.createdIn,
    query.includeOrderMetrics
  ]);
  const currentQueryKey = useRef(queryKey);
  currentQueryKey.current = queryKey;
  const scopedQueryKey = useMemo(
    () => options?.scope ? [listQueryKey, { scope: options.scope }] : undefined,
    [options?.scope]
  );
  const cancel = useCallback(
    () => queryClient.cancelQueries({
      queryKey: scopedQueryKey || currentQueryKey.current,
      exact: !scopedQueryKey
    }),
    [queryClient, scopedQueryKey]
  );
  const result = useInfiniteQuery<IQueryFnParams>({
    staleTime: 0,
    enabled: enabled,
    queryKey,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const currentRows = allPages.reduce((prev, curr) => prev + curr.customers.length, 0) || 0;
      if (lastPage.totalRowsCount <= currentRows) {
        return undefined
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
      if ((firstPageParam as number) <= 1) {
        return undefined
      }
      return (firstPageParam as number) - 1
    },
    queryFn: async ({ pageParam, signal }: { pageParam: number, signal: AbortSignal }) => {
      try {
        const res = await getCustomersV2({
          ...query,
          pageNum: pageParam,
          limit: query.limit,
          signal
        });

        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data as IQueryFnParams;
      } catch (e: any) {
        if (e?.name !== 'AbortError' && e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
          console.log('error caught', e['response']?.data?.['errors']);
        }
        throw e;
      }
    }
  });

  return { ...result, cancel };
};

export const useGetCustomeryId = (id: string, enabled = true) => useQuery<ICustomer>({
  queryKey: [listQueryKey, { _id: id }],
  enabled: !!id && enabled,
  queryFn: async ({ queryKey }) => {
    try {
      const [_key, { _id: _id }] = queryKey;
      const res = await getCustomerByIdV2(_id);
      if (!res?.data?.customer) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.customer;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const getCustomerHighlightsById = (id: string) => useQuery<any>({
  queryKey: [listQueryKey, { _id: id }],
  enabled: !!id,
  queryFn: async ({ queryKey }) => {
    try {
      const [_key, { _id: _id }] = queryKey;
      const res = await getCustomerHighlightsByIdV2(_id);
      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});

export const useGetCustomerOrders = (id: string, params: {
  limit: number
}) => useInfiniteQuery<IQueryOrdersFnParams>({
  queryKey: ['customerOrders', { _id: id }],
  enabled: !!id,
  initialPageParam: 0,
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    const currentRows = allPages.reduce((prev, curr) => prev + curr.orders.length, 0) || 0;
    if (lastPage.totalRowsCount <= currentRows) {
      return undefined
    }
    return lastPageParam + 1;
  },
  getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
    if ((firstPageParam as number) <= 1) {
      return undefined
    }
    return (firstPageParam as number) - 1
  },
  queryFn: async ({ queryKey, pageParam }: any) => {
    try {
      const [_key, { _id: _id }] = queryKey;
      const res = await getCustomerOrdersV2(_id, {
        pageNum: pageParam,
        limit: params.limit
      });
      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});


export const useGetCustomerLoyaltyHistory = (id: string) => useQuery<any>({
  queryKey: ['customerLoyaltyHistory', { _id: id }],
  enabled: !!id,
  queryFn: async ({ queryKey }) => {
    try {
      const [_key, { _id: _id }] = queryKey;
      const res = await getCustomerLoyaltyHistory(_id);
      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});
